import { useQuery, useQueryClient } from '@tanstack/react-query';

import { createMenuManagementAxiosInstance } from 'createAxiosInstance';
import { logException } from 'helpers/loggers';

import type { ActiveBuildResponse } from 'containers/MenuUpload/types';

const ACTIVE_BUILD_QUERY_KEY = 'activeMenuBuild';

export const activeMenuBuildQueryKey = (companyId: number | null, merchantId: number | null) => [
  ACTIVE_BUILD_QUERY_KEY,
  companyId,
  merchantId,
];

type UseGetActiveMenuBuildParams = {
  companyId: number | null;
  merchantId: number | null;
};

export const useGetActiveMenuBuild = ({ companyId, merchantId }: UseGetActiveMenuBuildParams) => {
  const queryClient = useQueryClient();

  const query = useQuery<ActiveBuildResponse | null>({
    queryKey: activeMenuBuildQueryKey(companyId, merchantId),
    queryFn: async () => {
      try {
        const axios = await createMenuManagementAxiosInstance();
        const response = await axios.get(
          `/company/${companyId}/merchant/${merchantId}/menu/build/active`,
          { params: { jobType: 'menu_build' } },
        );
        if (!response.data || !response.data.jobId) return null;
        return response.data;
      } catch (error) {
        logException({
          message: 'Failed to fetch active menu build',
          error,
          context: {},
          tags: {},
        });
        return null;
      }
    },
    enabled: Boolean(companyId && merchantId),
    refetchOnMount: 'always',
  });
  const refetch = () =>
    queryClient.invalidateQueries({ queryKey: activeMenuBuildQueryKey(companyId, merchantId) });

  return {
    activeBuild: query.data ?? null,
    isLoading: query.isLoading,
    refetch,
  };
};
